import PopupBasic from 'src/game/components/popups/PopupBasic';
import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import View from '@play-co/timestep-core/lib/ui/View';
import StateObserver from 'src/StateObserver';
import { ImageScaleView, ImageSliceView } from '@play-co/timestep-core/ui';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { parseAmount } from 'src/lib/utils';
import sounds from 'src/lib/sounds';
import { Feature } from 'src/replicant/analytics';
import { GachaItem } from './GachaItem';
import { trackPinchEvent } from 'src/lib/analytics/events';
import { startGemsPinchSequence } from '../../shop/Helpers';
import { GachaID } from 'src/replicant/gacha/gacha.ruleset';
import * as Inventory from 'src/replicant/inventory';
import { assertNever } from 'src/replicant/utils';
import { PurchaseGachaArgs } from '../gacha.sequence';
import { Currency } from 'src/replicant/data/Gachas.schema';
import { createEmitter } from 'src/lib/Emitter';

import { AssetGroup } from '@play-co/timestep-core';

type CloseHandler = (result: boolean) => void;
const ui = uiConfig.buruburu.care.confirmation;

export default class PopupConfirmGacha extends PopupBasic {
  private close: CloseHandler;
  private cost: LangBitmapFontTextView;
  private balance: LangBitmapFontTextView;
  private okButton: ButtonScaleViewWithText;
  private currencyIcon: ImageScaleView[] = [];
  private costText: LangBitmapFontTextView;

  constructor(opts: { superview: View; close: CloseHandler }) {
    super({
      superview: opts.superview,
      close: () => opts.close(false),
      ...ui.popup,
      titleIcon: {
        image: `assets/ui/shared/icons/confirmation_icon.png`,
        width: 63,
        height: 53,
      },
      tracking: {
        feature: Feature.GACHA._,
        $subFeature: Feature.GACHA.PURCHASE,
      },
    });

    this.close = opts.close;
    this.buttonClose.removeFromSuperview();

    this.message.updateOpts({
      ...ui.message,
      width: this.box.style.width - 2 * ui.message.x,
    });

    this.box.addSubview(
      new ImageScaleView({
        ...ui.underline,
        width: this.message.style.width,
      }),
    );

    const costRow = this.box.addSubview(new ImageSliceView(ui.costRow));

    this.costText = costRow.addSubview(
      new LangBitmapFontTextView({
        ...ui.highlightRowLabel,
        localeText: () => i18n('gacha.confirmation.requiredGems'),
      }),
    );

    this.cost = new LangBitmapFontTextView({
      ...ui.highlightRowValue,
      superview: costRow,
    });

    this.currencyIcon.push(
      costRow.addSubview(
        new ImageScaleView({
          ...ui.currency,
          image: 'assets/ui/hud/icons/hud_gem.png',
          x: costRow.style.width - ui.currency.width,
        }),
      ),
    );

    const balanceRow = this.box.addSubview(new ImageSliceView(ui.balanceRow));

    balanceRow.addSubview(
      new LangBitmapFontTextView({
        ...ui.highlightRowLabel,
        localeText: () => i18n('gacha.confirmation.balanceAfterPurchase'),
      }),
    );

    this.balance = new LangBitmapFontTextView({
      ...ui.highlightRowValue,
      superview: balanceRow,
    });

    this.currencyIcon.push(
      balanceRow.addSubview(
        new ImageScaleView({
          ...ui.currency,
          image: 'assets/ui/hud/icons/hud_gem.png',
          x: balanceRow.style.width - ui.currency.width,
        }),
      ),
    );

    this.box.addSubview(
      new ButtonScaleViewWithText({
        ...ui.backButton,
        localeText: () => i18n('basic.back'),
        onClick: async () => {
          sounds.sfx.play('cancel');
          return opts.close(false);
        },
        tracking: {
          feature: Feature.GACHA._,
          $subFeature: Feature.GACHA.PURCHASE_BACK,
        },
      }),
    );

    this.okButton = new ButtonScaleViewWithText({
      ...ui.okButton,
      superview: this.box,
      localeText: () => i18n('basic.okay'),
      onClick: async () => {
        sounds.sfx.play('ok');
        return opts.close(true);
      },
      tracking: {
        feature: Feature.GACHA._,
        $subFeature: Feature.GACHA.PURCHASE_OK,
      },
    });
  }

  protected getAssetsGroup() {
    return new AssetGroup([
      'assets/ui/shared/icons/confirmation_icon.png',
      'assets/ui/hud/icons/hud_gem.png',
    ]);
  }

  public init({ gacha }: { gacha: PurchaseGachaArgs }) {
    super.init({
      title: i18n('gacha.confirmation.title'),
      message: i18n('gacha.confirmation.message'),
    });

    this.box.addSubview(
      new GachaItem({
        x: 40,
        y: 170,
        title: i18n(`gacha.gachas.${gacha.num_spins === 1 ? 'single' : '10'}`),
        single: gacha.num_spins === 1,
        newPrice: gacha.price,
        oldPrice: gacha.ref_price,
        currency: gacha.currency,
        tracking: {
          feature: Feature.GACHA._,
          $subFeature: Feature.GACHA.SELECT_POPUP.ITEM,
        },
      }),
    ).disabled = true;

    createEmitter(this.getView(), ({ user }) =>
      Inventory.getters.getGems(user),
    ).addListener(() => {
      this.cost.localeText = () => parseAmount(gacha.price);

      const balance = this.getBalanceAfterPurchase(gacha.currency, gacha.price);
      this.balance.localeText = () => parseAmount(balance);

      if (!this.canPurchase(gacha.currency, gacha.price)) {
        this.balance.updateOpts({ color: 'red' });
      } else {
        this.balance.updateOpts({ color: '#9A7171' });
      }

      this.setCurrency(gacha.currency);
    });

    this.okButton.onClick = async () => {
      sounds.sfx.play('ok');

      // User cannot afford the purchase. Show pinch sequence.
      if (!this.canPurchase(gacha.currency, gacha.price)) {
        this.trackPinchEvent(gacha.currency, gacha.id);
        await startGemsPinchSequence({
          feature: 'gacha',
          pinch: 'gems',
          gemsNeeded: Math.abs(
            this.getBalanceAfterPurchase(gacha.currency, gacha.price),
          ),
          crossplayTracking: {
            feature: Feature.GACHA._,
            subFeature: Feature.GACHA.PURCHASE,
          },
          additionalContext: [{ type: 'promptPurchaseGacha', gacha }],
        });

        // Still can't purchase. Do nothing.
        if (!this.canPurchase(gacha.currency, gacha.price)) {
          return;
        }
      }

      // Close popup and proceed with purchase.
      this.close(true);
    };
  }

  /**
   * Check if player can purchase care item
   */
  private canPurchase(currency: Currency, price: number) {
    return this.getBalanceAfterPurchase(currency, price) >= 0;
  }

  /**
   * Get balance after purchase, can be negative
   */
  private getBalanceAfterPurchase(currency: Currency, price: number) {
    switch (currency) {
      case 'gems':
        this.costText.localeText = () =>
          i18n('gacha.confirmation.requiredGems');
        return Inventory.getters.getGems(StateObserver.getState().user) - price;

      case 'bronze_ticket':
      case 'silver_ticket':
        throw new Error('Not implemented');

      default:
        assertNever(currency);
    }
  }

  private setCurrency(currency: Currency) {
    switch (currency) {
      case 'gems':
        this.currencyIcon.forEach((x) =>
          x.updateOpts({
            image: 'assets/ui/hud/icons/hud_gem.png',
          }),
        );
        break;

      case 'bronze_ticket':
      case 'silver_ticket':
        throw new Error('Not implemented');

      default:
        assertNever(currency);
    }
  }

  private trackPinchEvent(currency: Currency, gachaId: GachaID) {
    if (currency !== 'gems') {
      throw new Error('Not implemented');
    }

    trackPinchEvent({
      resource: currency,
      trigger: 'gacha',
      item_id: gachaId,
    });
  }
}
